// src/components/ui/pagination.jsx - Mobile-optimized pagination component
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

export function Pagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = true,
  showInfo = true,
  isMobile = false
}) {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Mobile: Show only essential page numbers
  const getVisiblePages = () => {
    if (isMobile) {
      // On mobile, show max 3 pages around current page
      const delta = 1;
      const range = [];
      
      for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
        range.push(i);
      }
      
      if (currentPage - delta > 2) {
        range.unshift("...");
      }
      if (currentPage + delta < totalPages - 1) {
        range.push("...");
      }
      
      range.unshift(1);
      if (totalPages > 1) {
        range.push(totalPages);
      }
      
      return range;
    } else {
      // Desktop: Show more pages
      const delta = 2;
      const range = [];
      
      for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
        range.push(i);
      }
      
      if (currentPage - delta > 2) {
        range.unshift("...");
      }
      if (currentPage + delta < totalPages - 1) {
        range.push("...");
      }
      
      range.unshift(1);
      if (totalPages > 1) {
        range.push(totalPages);
      }
      
      return range;
    }
  };

  const visiblePages = getVisiblePages();

  if (totalPages <= 1 && !showInfo && !showPageSizeSelector) {
    return null;
  }

  return (
    <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'flex-col sm:flex-row'} items-center justify-between gap-3`}>
      {/* Info and Page Size Selector */}
      {(showInfo || showPageSizeSelector) && (
        <div className={`flex ${isMobile ? 'flex-col space-y-2 text-center' : 'flex-col sm:flex-row'} items-center gap-3 text-sm text-slate-600`}>
          {showInfo && (
            <span className={isMobile ? 'text-xs' : ''}>
              {totalItems > 0 ? (
                <>Visualizzando {startItem}-{endItem} di {totalItems} risultati</>
              ) : (
                <>Nessun risultato</>
              )}
            </span>
          )}
          
          {showPageSizeSelector && totalItems > 0 && (
            <div className="flex items-center gap-2">
              <span className={isMobile ? 'text-xs' : ''}>Mostra:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => onPageSizeChange(parseInt(value))}
              >
                <SelectTrigger className={`${isMobile ? 'w-16 h-8 text-xs' : 'w-20 h-8'}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map(option => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          {/* Previous Button */}
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`${isMobile ? 'px-2' : 'px-3'}`}
          >
            <ChevronLeft className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
            {!isMobile && <span className="ml-1">Precedente</span>}
          </Button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1">
            {visiblePages.map((page, index) => (
              <div key={index}>
                {page === "..." ? (
                  <div className={`${isMobile ? 'px-1' : 'px-2'} py-2`}>
                    <MoreHorizontal className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-400`} />
                  </div>
                ) : (
                  <Button
                    variant={currentPage === page ? "default" : "outline"}
                    size={isMobile ? "sm" : "default"}
                    onClick={() => onPageChange(page)}
                    className={`${isMobile ? 'w-8 h-8 p-0 text-xs' : 'w-10 h-10 p-0'} ${
                      currentPage === page 
                        ? 'bg-blue-500 text-white border-blue-500' 
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    {page}
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Next Button */}
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`${isMobile ? 'px-2' : 'px-3'}`}
          >
            {!isMobile && <span className="mr-1">Successiva</span>}
            <ChevronRight className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
          </Button>
        </div>
      )}
    </div>
  );
}